// External libraries
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Map, OrderedMap, Seq } from 'immutable';
import cx from 'classnames';
import { canUseDOM } from 'exenv';
import get from 'lodash/get';
import url from 'url';

// Internal libraries
import bookConfirmationExternals from 'lib/book-confirmation-externals';
import { PAGEVIEW_EVENT } from 'lib/analytics/events';
import { pageProps, urlProps } from 'lib/analytics/page-properties';
import { routerLocationShape } from 'lib/common/prop-types';

import VenueModel from 'models/venue';
import ReceiptModel from 'models/receipt';
import UserModel from 'models/user';
import Search from 'models/search';
import Brand from 'models/brand';
import TrackingProperties from 'models/tracking-properties';

// Components
import NextSteps from 'components/receipt/next-steps';
import HelpInfo from 'components/receipt/help-info';
import BookingDetails from 'components/receipt/booking-details';
import PurchaseSummary from 'components/receipt/purchase-summary';
import ReceiptButtons from 'components/receipt/receipt-buttons';
import ViewReceipt from 'components/bookings/links/view-receipt';
import PrintLink from 'components/common/links/print-link';
import LicensePlateModal from 'components/vehicles/modal/license-plate';
import IFrame from 'components/common/iframe';
import BookAgain from 'components/receipt/book-again';

import trackEventCreator from 'action-creators/analytics/track-event';
import cleanupReciptState from 'action-creators/bookings/cleanup-receipt-state';
import addVehicle from 'action-creators/bookings/add-vehicle';

import ResetActiveAccountModal from 'components/receipt/reset-active-account-modal';
import resetPasswordCreator from 'action-creators/account/reset-password';
import activateAccountModal from 'action-creators/bookings/activate-account-modal';
import dismissActivateAccountCreator from 'action-creators/bookings/dismiss-activate-account';
import NameOnReceipt from 'components/receipt/name-on-receipt';

const propTypes = {
  bookings: PropTypes.instanceOf(Seq.Keyed).isRequired,
  parkingPasses: PropTypes.instanceOf(OrderedMap).isRequired,
  locations: PropTypes.instanceOf(OrderedMap).isRequired,
  receipt: PropTypes.instanceOf(ReceiptModel).isRequired,
  trackingProperties: PropTypes.instanceOf(TrackingProperties).isRequired,
  events: PropTypes.instanceOf(OrderedMap),
  venue: PropTypes.instanceOf(VenueModel),
  venues: PropTypes.instanceOf(Map),
  user: PropTypes.instanceOf(UserModel),
  vehicles: PropTypes.instanceOf(OrderedMap),
  receiptUser: PropTypes.instanceOf(UserModel),
  currentSearch: PropTypes.instanceOf(Search),
  routerLocation: routerLocationShape.isRequired,
  trackEvent: PropTypes.func.isRequired,
  addVehicle: PropTypes.func.isRequired,
  cleanupReciptState: PropTypes.func.isRequired,
  resetPassword: PropTypes.func.isRequired,
  brand: PropTypes.instanceOf(Brand).isRequired,
  accountActivated: PropTypes.bool.isRequired,
  activateAccountModalDismissed: PropTypes.bool.isRequired,
  shouldShowActivateAccountModal: PropTypes.bool.isRequired,
  activateAccountModal: PropTypes.func.isRequired,
  dismissActivateAccount: PropTypes.func.isRequired,
};

const defaultProps = {
  events: new OrderedMap(),
  venue: null,
  venues: new OrderedMap(),
  user: null,
  vehicles: OrderedMap(),
  receiptUser: null,
  currentSearch: null,
};

class Receipt extends Component {
  constructor(props) {
    super(props);

    this.user = props.receiptUser || props.user;

    this.submitForm = this.submitForm.bind(this);
    this.showActivateAccountModal = this.showActivateAccountModal.bind(this);
  }

  componentWillMount() {
    if (canUseDOM) {
      const { currentSearch, bookings } = this.props;
      const bookingIDs = bookings.map(b => (b.id)).join(',');
      const bookingCount = bookings.size;

      const pageProperties = pageProps({ app: 'Receipt' });
      const trackingEvent = {
        ...PAGEVIEW_EVENT,
        ...pageProperties,
        name: pageProperties.pageName,
        properties: {
          ...urlProps(),
          bookingIDs,
          bookingCount,
        },
      };

      if (currentSearch && currentSearch.selectedQuoteId) {
        trackingEvent.properties.quoteID = currentSearch.selectedQuoteId;
      }

      this.props.trackEvent(trackingEvent);
    }
  }

  componentDidMount() {
    if (this.isThankYou()) {
      try {
        bookConfirmationExternals({
          user: this.user,
          receipt: this.props.receipt,
          bookings: this.props.bookings,
        });
      } catch (e) {}
    }
  }

  componentWillUnmount() {
    this.props.cleanupReciptState();
  }

  renderLicensePlateModal() {
    const { parkingPasses } = this.props;
    let render = false;
    parkingPasses.forEach((parkingPass) => {
      if (parkingPass.requireLicensePlate()) {
        render = true;
        return true;
      }
      return false;
    });

    if (!render) {
      this.showActivateAccountModal();
      return null;
    }

    return (
      <LicensePlateModal
        user={this.user}
        vehicles={this.props.vehicles}
        submitForm={this.submitForm}
        onClose={this.showActivateAccountModal}
      />
    );
  }

  showActivateAccountModal() {
    if (!this.props.accountActivated && !this.props.activateAccountModalDismissed) {
      this.props.activateAccountModal();
    }
  }

  renderPasswordResetModal() {
    const {
      user,
      brand,
      dismissActivateAccount,
      resetPassword,
      shouldShowActivateAccountModal,
      trackEvent,
    } = this.props;
    if (user.isLoggedIn()) {
      return null;
    }
    return (
      <ResetActiveAccountModal
        shouldShowActivateAccountModal={shouldShowActivateAccountModal}
        resetPassword={resetPassword}
        dismissActivateAccount={dismissActivateAccount}
        user={user}
        brand={brand}
        trackEvent={trackEvent}
      />
    );
  }

  isThankYou() {
    return this.props.routerLocation.pathname === '/reserve/thankyou/';
  }

  submitForm(input) {
    const { bookings } = this.props;
    this.props.addVehicle({
      ...input,
      bookingId: bookings.first().id,
    });
    this.showActivateAccountModal();
  }

  render() {
    if (!this.props.bookings.size) { return null; }

    const { bookings, parkingPasses, locations, events, venue, venues, receipt, brand, routerLocation } = this.props;
    const { user } = this;
    const location = bookings.first().getLocation(locations);
    const bookingCards = bookings.toIndexedSeq().map((booking, index) => (
      <BookingDetails
        key={`booking-details-${booking.id}`}
        booking={booking}
        location={location}
        event={booking.getEvent(events)}
        venue={venue || booking.getVenue(venues, events)}
        analyticsPosition={index}
      />
    ));

    const summaryColClasses = cx({
      'col-sm-7': true,
      'col-print-7': true,
      'force-show-print': true,
      'hidden-lte-sm': !this.isThankYou(),
    });

    const receiptColClasses = cx({
      'col-sm-5': true,
      'col-print-5': true,
      'force-show-print': true,
      'padding-0-lte-sm': true,
      'hidden-lte-sm': this.isThankYou(),
    });

    const whatsNextClasses = cx({
      'row': true,
      'force-show-print': true,
      'whats-next-section': true,
      'hidden-lte-sm': !this.isThankYou(),
    });

    const { u: authorizationCode } = url.parse(routerLocation.search, true).query || {};

    return (
      <div className="container">
        { this.renderLicensePlateModal() }
        { this.renderPasswordResetModal() }
        <BookAgain brand={brand} isThankYou={this.isThankYou()} />

        <div className={whatsNextClasses}>
          <h3 className="col-xs-12 h3">We&#39;ve sent an email confirmation to <strong>{user.email}</strong></h3>
          <NameOnReceipt user={user} booking={bookings.first()} />
          <div className="col-md-9">
            <h2 className="h2 top">What&#39;s next...</h2>
            <NextSteps
              bookings={bookings}
              parkingPasses={parkingPasses}
              user={user}
              brand={brand}
            />
          </div>
          <div className="col-xs-3">
            <div className="hidden-lte-sm">
              <ReceiptButtons
                authorizationCode={authorizationCode}
                bookings={bookings}
                parkingPasses={parkingPasses}
                brand={brand}
                trackingProperties={this.props.trackingProperties}
              />
            </div>
          </div>
        </div>

        <div className="row hidden-lte-sm force-show-print">
          <div className="col-xs-12">
            <h3 className="h3">Here are some facts about your booking...</h3>
          </div>
        </div>

        <div className="row">
          <div className={summaryColClasses}>
            <div className="hidden-lte-sm force-show-print">
              {bookingCards}
            </div>
            <div className="visible-lte-sm hidden-print">
              <div className="vertical-padding-top align-center greentext uppercase vertical-padded-link">
                <ViewReceipt booking={bookings.first()} />
              </div>
              <ReceiptButtons
                authorizationCode={authorizationCode}
                bookings={bookings}
                parkingPasses={parkingPasses}
                brand={brand}
                trackingProperties={this.props.trackingProperties}
              />
            </div>
            <IFrame url={bookings.first().monthlyIFrameURI} />
            <HelpInfo booking={bookings.first()} brand={brand} />
          </div>
          <div className={receiptColClasses}>
            <h4 className="h4 visible-lte-sm align-right horizontal-padding hidden-print">RECEIPT</h4>
            <PurchaseSummary
              bookings={bookings}
              receipt={receipt}
              user={user}
              location={location}
            />
            <div className="hidden-lte-sm green-text align-right hidden-print"><PrintLink label={'PRINT RECEIPT'} classNames="text-weight-bold" /></div>
            <div className="visible-lte-sm hidden-print">
              <ReceiptButtons
                authorizationCode={authorizationCode}
                bookings={bookings}
                parkingPasses={parkingPasses}
                brand={brand}
                trackingProperties={this.props.trackingProperties}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }
}

Receipt.propTypes = propTypes;
Receipt.defaultProps = defaultProps;

const mapStateToProps = (state, ownProps) => {
  const { brand } = state.brand;

  const {
    user,
    vehicles,
    accountActivated,
  } = state.account;

  const {
    bookings,
    locations,
    parkingPasses,
    receipt,
    events,
    venue,
    venues,
    activateAccountModalDismissed,
    shouldShowActivateAccountModal,
  } = state.bookings;

  let { location: routerLocation } = state.router;
  if (!routerLocation) {
    ({ routerLocation } = ownProps);
  }

  const {
    insights,
    trackingProperties,
  } = state.analytics;

  return {
    user,
    vehicles,
    bookings,
    locations,
    parkingPasses,
    receipt,
    events,
    venue,
    venues,
    receiptUser: get(receipt, 'user', user),
    routerLocation,
    accountActivated,
    brand,
    activateAccountModalDismissed,
    shouldShowActivateAccountModal,
    insights,
    trackingProperties,
  };
};

const mapDispatchToProps = dispatch => (
  bindActionCreators({
    cleanupReciptState,
    addVehicle,
    trackEvent: trackEventCreator,
    resetPassword: resetPasswordCreator,
    activateAccountModal,
    dismissActivateAccount: dismissActivateAccountCreator,
  }, dispatch)
);

export default connect(mapStateToProps, mapDispatchToProps)(Receipt);

